import React, { Fragment, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import IconButton from '@material-ui/core/IconButton';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Typography
} from '@material-ui/core';
import Table from 'components/Table/Table.js';
import CategoryPicker from 'views/Dashboard/Overview/RunningPairs/CategoryPicker';
import GunbotStorage from 'assets/js/gunbot.storage';
import ServerSideTable from 'components/Table/ServerSideTable';
import Api from 'api/index';
import { useState } from 'react';
import { selectBalanceInfo, selectPairItem } from 'redux/selectors';
import { getDateTimeString } from 'assets/js/common';
import { AccountBalanceWallet, ExpandMore } from '@material-ui/icons';
import scientificToDecimal from 'scientific-to-decimal';

import { useSnackbar } from 'notistack';

const colorScheme = {
  text: '#E6E8EA',
  textSubtle: '#98A2AD',
  yellow: '#E6B21A',
  green: '#14C784',
  greenHover: '#12B077',
  red: '#E84D4F',
  redHover: '#CC4244',
  tile: '#1E2329',
  tileHover: '#232A31',
  border: '#2A2F36',
  borderSoft: '#3A4048',
  inputBg: '#101418',
  divider: '#2A2F36'
};

const getColor = data => {
  if (data)
    return data.toUpperCase() === 'BUY' ? 'orders.buy' : 'orders.sell';
  else return 'orders.sell'
};

export default function TradingTerminal(props) {
  let { pair, exchange, isCoinM, hasLong, hasShort, marketType = '', currentQty = 0 } = props;
  const selectedBalanceInfo = useSelector(selectBalanceInfo(exchange, pair));
  const key = `${exchange}/${pair}`;
  const openOrders = useSelector(selectPairItem(key, 'openOrders'));
  const orderBook = useSelector(selectPairItem(key, 'orderbook'));
  const isMargin = !marketType.includes('spot')
  const Ask = useSelector(selectPairItem(key, 'Ask'));
  const Bid = useSelector(selectPairItem(key, 'Bid'));
  const showSinglePairInterface = useSelector(state => state.settings.corememData.licSinglePairEdition);

  const [base, quote = 'Loading...'] = (pair || '-').split('-');
  const { baseBalance = 0, quoteBalance = 0 } = selectedBalanceInfo || {};
  const [pendingCancelOrders, setPendingCancelOrders] = useState([]);
  const [place, setPlace] = useState('limit');
  const [buyAmount, setBuyAmount] = useState('');
  const [buyPrice, setBuyPrice] = useState(Ask);
  const [closeBuy, setCloseBuy] = useState(false);
  const [sellAmount, setSellAmount] = useState('');
  const [sellPrice, setSellPrice] = useState(Bid);
  const [closeSell, setCloseSell] = useState(false);
  const [orderbookBackground, setOrderbookBackground] = useState({
    ask: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    bid: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  });
  const [tradingHistoryHeads, setTradingHistoryHeads] = useState([]);

  const [spread, setSpread] = useState(0);
  const [spreadPct, setSpreadPct] = useState('');
  const [currentOpenOrders, setCurrentOpenOrders] = useState([]);
  const [currentPair, setCurrentPair] = useState('');
  const [data, setData] = useState({});
  const [showLong, setShowLong] = useState(true);
  const [showShort, setShowShort] = useState(true);
  const [hoverBuy, setHoverBuy] = useState(false);
  const [hoverSell, setHoverSell] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const inputShellStyle = {
    background: colorScheme.inputBg,
    border: `1px solid ${colorScheme.border}`,
    borderRadius: 8
  };
  const inputLabelStyle = { color: colorScheme.textSubtle };
  const adornmentStyle = { color: colorScheme.textSubtle };
  const chipStyle = {
    cursor: 'pointer',
    borderRadius: 6,
    background: colorScheme.tile,
    color: colorScheme.text,
    border: `1px solid ${colorScheme.borderSoft}`
  };
  // segmented control styles
  const segWrapStyle = {
    display: 'flex',
    background: colorScheme.tile,
    border: `1px solid ${colorScheme.border}`,
    borderRadius: 10,
    paddingTop: 0,
    justifyContent: "flex-end",
    maxWidth: 'max-content',
    marginLeft: 'auto'
  };
  const segBtnBase = {
    textTransform: 'none',
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: 8,
    minWidth: 84
  };


  const sendNotification = function (text, variant) {
    enqueueSnackbar(text, {
      variant: variant || 'success',
      preventDuplicate: true,
      style: { whiteSpace: 'pre-line' },
    });
  };

  const resetForm = function () {
    setBuyAmount('');
    setBuyPrice(Ask);
    setCloseBuy(false);
    setSellAmount('');
    setSellPrice(Bid);
    setCloseSell(false);
  };

  const resetAmount = function () {
    setBuyAmount('');
    setSellAmount('');
  };

  const handleCloseBuyCheckbox = function (newValue) {
    setCloseBuy(newValue)
    resetAmount()
  }

  const handleCloseSellCheckbox = function (newValue) {
    setCloseSell(newValue)
    resetAmount()
  }

  // expand value for orderBook of the overview
  const [orderBookExpand, setOrderBookExpand] = useState(true);
  const handleBuyPercent = useCallback(
    percent => () => {
      if (isCoinM) {
        const amt = Math.abs(quoteBalance * percent) / 100;
        setBuyAmount(amt);
      }
      else if (!closeBuy && place === 'limit') {
        let price = buyPrice || Ask;
        let baseValue = ((baseBalance / price * percent) / 100);
        let bybitAmount = (baseBalance * percent) / 100;
        setBuyPrice(price);
        if (exchange?.includes('bybit')) setBuyAmount((bybitAmount).toFixed(8));
        else setBuyAmount((bybitAmount / price).toFixed(8));
      }
      else if (!closeBuy && place === 'market') {
        let price = buyPrice || Ask;
        let baseValue = ((baseBalance / price * percent) / 100);
        let bybitAmount = (baseBalance * percent) / 100;
        if (exchange?.includes('bybit')) setBuyAmount((bybitAmount).toFixed(8));
        else setBuyAmount((bybitAmount / price).toFixed(8));
        setBuyPrice(price);
      }
      else if (closeBuy) {
        let baseValue = (currentQty * percent) / 100;
        let price = buyPrice || Ask;
        setBuyPrice(price);
        setBuyAmount(baseValue.toFixed(8));
      }
      else {
        let baseValue = (quoteBalance * percent) / 100;
        let price = buyPrice || Ask;
        setBuyPrice(price);
        setBuyAmount(baseValue.toFixed(8));
      }
    },
    [setBuyAmount, baseBalance, Ask, buyPrice, closeBuy, place],
  );

  const handleSellPercent = useCallback(
    percent => () => {
      if (isCoinM) {
        const amt = Math.abs(quoteBalance * percent) / 100;
        setSellAmount(amt);
      }
      else if (closeSell) {
        let baseValue = (Math.abs(currentQty) * percent) / 100;
        let price = sellPrice || Bid;
        setSellPrice(price);
        setSellAmount(baseValue.toFixed(8));
      }
      else if (exchange?.includes('bitget') && !marketType.includes('spot')) {
        let baseValue = (Math.abs(baseBalance) * percent) / 100;
        let price = sellPrice || Bid;
        setSellPrice(price);
        setSellAmount(baseValue.toFixed(8));
      }
      else if (!exchange?.includes('colorSchemeFutures') && !exchange?.includes('futures_gunthy')) {
        let price = sellPrice || Bid;
        setSellPrice(price);
        if (exchange?.includes('bybit') && !closeSell && !marketType.includes('spot')) {
          setSellAmount(((baseBalance * percent) / 100).toFixed(8));
        } else {
          setSellAmount(((quoteBalance * percent) / 100).toFixed(8));
        }
      }
      else {
        if (!closeSell) {
          let price = sellPrice || Bid;
          let baseValues = (baseBalance * percent) / 100;
          setSellAmount((baseValues / price).toFixed(8));
          setSellPrice(price);
        } else {
          setSellAmount(((quoteBalance * percent) / 100).toFixed(8));
          let price = sellPrice || Bid;
          setSellPrice(price);
        }
      }
    },
    [setSellAmount, baseBalance, quoteBalance, Bid, sellPrice, closeSell],
  );

  const handleCloseOrder = useCallback(() => {
    function request() {
      let closeSide = quoteBalance < 0 ? 'sell' : 'buy';
      let requestData = {
        amt: parseFloat(buyAmount),
        price: buyPrice,
        exch: exchange,
        pair: pair,
      };
      if (exchange?.includes('bybit') || exchange?.includes('bitget')) {
        if (place === 'limit' && closeSide === 'buy') {
          requestData.amt = Math.abs(buyAmount);
          requestData.price = buyPrice;
          sendNotification('CLOSE order sent side LONG', 'info');
          return Api.placeCloseOrder(requestData);
        } else if (place === 'limit' && closeSide === 'sell') {
          requestData.amt = Math.abs(sellAmount) * -1;
          requestData.price = sellPrice;
          sendNotification('CLOSE order sent', 'info');
          return Api.placeCloseOrder(requestData);
        } else if (place !== 'limit' && closeSide === 'sell') {
          requestData.amt = Math.abs(sellAmount) * -1;
          sendNotification('CLOSE order sent', 'info');
          return Api.placeCloseMarketOrder(requestData);
        } else if (place !== 'limit' && closeSide === 'buy') {
          requestData.amt = Math.abs(buyAmount);
          sendNotification('CLOSE order sent', 'info');
          return Api.placeCloseMarketOrder(requestData);
        }
      } else if (!exchange?.includes('colorSchemeFutures') && !exchange?.includes('futures_gunthy')) {
        if (place === 'limit') {
          sendNotification('CLOSE order sent', 'info');
          return Api.placeCloseOrder(requestData);
        } else {
          sendNotification('CLOSE order sent', 'info');
          return Api.placeCloseMarketOrder(requestData);
        }
      } else {
        if (place === 'limit' && closeSide === 'buy') {
          requestData.amt = buyAmount;
          sendNotification('CLOSE order sent', 'info');
          return Api.placeSellOrder(requestData);
        } else if (place === 'limit' && closeSide === 'sell') {
          requestData.amt = Math.abs(sellAmount);
          sendNotification('CLOSE order sent', 'info');
          return Api.placeBuyOrder(requestData);
        } else if (place !== 'limit' && closeSide === 'sell') {
          requestData.amt = Math.abs(sellAmount);
          sendNotification('CLOSE order sent', 'info');
          return Api.placeMarketBuyOrder(requestData);
        } else if (place !== 'limit' && closeSide === 'buy') {
          requestData.amt = buyAmount;
          sendNotification('CLOSE order sent', 'info');
          return Api.placeMarketSellOrder(requestData);
        }
      }
    }

    request().then(response => {
      if (response.data === 'false') {
        console.error('Something went wrong.');
      }
    });
  }, [quoteBalance, place, buyAmount, buyPrice, sellAmount, sellPrice, exchange, pair, closeBuy, closeSell]);

  const handleBuyOrder = useCallback(() => {
    if (buyAmount === '') {
      return;
    }
    let amount = parseFloat(buyAmount);

    if (exchange == 'nashio') amount = parseFloat(buyAmount) * parseFloat(Ask);
    const requestData = {
      amt: isCoinM
        ? amount
        : place === 'limit' && !exchange?.includes('bybit')
          ? amount
          : exchange?.includes('bybit') && place == 'market'
            ? amount
            : amount,
      price: place === 'limit' ? parseFloat(buyPrice) : Bid,
      exch: exchange,
      pair: pair,
    };

    if (exchange?.includes('bitget') && marketType === 'swap' && place === 'market') {
      requestData.amt = parseFloat(buyAmount) / parseFloat(Ask)
    }
    if (closeBuy) {
      handleCloseOrder();
    } else {
      if (place === 'limit') {
        sendNotification('BUY order sent', 'success');
        return Api.placeBuyOrder(requestData);
      } else {
        sendNotification('BUY order sent', 'success');
        return Api.placeMarketBuyOrder(requestData);
      }
    }
    resetForm();
  }, [buyAmount, buyPrice, exchange, pair, place, handleCloseOrder, closeBuy, marketType]);

  const handleSellOrder = useCallback(() => {
    if (sellAmount === '') {
      return;
    }
    let requestData = {
      amt: parseFloat(sellAmount),
      price: place === 'limit' ? parseFloat(sellPrice) : Ask,
      exch: exchange,
      pair: pair,
    };
    if (closeSell) {
      handleCloseOrder();
    } else {
      if (place === 'limit') {
        sendNotification('SELL order sent', 'error');
        return Api.placeSellOrder(requestData);
      } else {
        sendNotification('SELL order sent', 'error');
        return Api.placeMarketSellOrder(requestData);
      }
    }
    resetForm();
  }, [sellAmount, sellPrice, exchange, pair, handleCloseOrder, place, closeSell]);

  const cancelOrder = useCallback(
    order => () => {
      const data = {
        price: order.rate,
        id: order.id,
        exch: exchange,
        pair: pair,
        type: order.type,
      };

      return Api.cancelOrder(data)
        .then(() => {
          sendNotification('Cancel order sent', 'info');
          setPendingCancelOrders([...pendingCancelOrders, order.id]);
          setTimeout(() => {
            setPendingCancelOrders(pendingCancelOrders.filter(item => item !== order.id));
          }, 10 * 1000);
        })
        .catch(error => {
          console.error('Error: ', error);
        });
    },
    [exchange, pair, pendingCancelOrders],
  );

  const handleHeadsTradingHistory = useCallback(data => {
    GunbotStorage.set('TRADING_HISTORY', data);
    setTradingHistoryHeads(data);
  }, []);

  useEffect(() => {
    let heads = [];
    let defaultHeads = [labelsTradingHistory[0], labelsTradingHistory[2], labelsTradingHistory[3]];
    heads = GunbotStorage.get('TRADING_HISTORY', defaultHeads);
    setTradingHistoryHeads(heads);
  }, []);

  useEffect(() => {
    const temp = [];
    if (openOrders) {
      openOrders.forEach(item => {
        const rate = (
          <Box color={`${getColor(item.type)}`} component={'span'}>
            {item.rate}
          </Box>
        );
        const time = getDateTimeString(item.time);
        const control = (
          <IconButton color="inherit" aria-label="cancel order" onClick={cancelOrder(item)}>
            {pendingCancelOrders.indexOf(item.id) >= 0 ? <HourglassEmptyIcon /> : <HighlightOffIcon />}
          </IconButton>
        );
        temp.push([rate, item.amount, time, control]);
      });
    }
    setCurrentOpenOrders(temp);
  }, [exchange, pair, openOrders, cancelOrder, pendingCancelOrders]);

  useEffect(() => {
    // reset form values after changing pair
    let pairChanged = false;
    if (currentPair !== pair) {
      setCurrentPair(pair);
      pairChanged = true;
    }

    if (!_.isNil(pair) && pairChanged) {
      if (pair.endsWith('-SHORT')) {
        setShowLong(false);
      } else {
        setShowLong(true);
      }
      if (pair.endsWith('-LONG')) {
        setShowShort(false);
      } else {
        setShowShort(true);
      }
      resetForm();
    }
  }, [props]);

  useEffect(() => {
    const isAbleToBuy = buyAmount && parseFloat(buyAmount) > 0;
    const isAbleToSell = sellAmount && parseFloat(sellAmount) > 0;
    let displaySymbolBuy = quote;
    let displaySymbolSell = quote;
    if (exchange?.includes('bybit')) {
      if (!closeBuy && isMargin) {
        displaySymbolBuy = base;
      }
      if (!closeSell && isMargin) {
        displaySymbolSell = base;
      }
    } else if (isCoinM) {
      displaySymbolBuy = 'Contracts';
      displaySymbolSell = 'Contracts';
    } else if (place === 'market' && !closeBuy) {
      displaySymbolBuy = quote;
      displaySymbolSell = quote;
    }

    const temp = {};
    ['limit', 'market'].forEach(tabName => {
      temp[tabName] = [
        {
          type: 'component',
          component: (
            <Box display="flex" flexWrap="wrap" mt={-4} mb={-2} mx={{ xs: -3 }}>
              <>
                {showLong && (
                  <Box width={{ xs: '100%' }} mb={0} style={{ padding: '2.5rem' }}>
                    {isCoinM ? (
                      <Box display="flex" alignItems="center" mb={2} style={{ color: colorScheme.text }}>
                        <AccountBalanceWallet style={{ color: colorScheme.textSubtle, marginRight: 6 }} />{' '}
                        <span>
                          {quote}: {baseBalance}
                        </span>
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" mb={2} style={{ color: colorScheme.text }}>
                        <AccountBalanceWallet style={{ color: colorScheme.textSubtle, marginRight: 6 }} />{' '}
                        <span>
                          {base}: {baseBalance}
                        </span>
                      </Box>
                    )}
                    {place === 'limit' && (
                      <Box mb={2}>
                        <FormControl variant="outlined" size={'small'} fullWidth style={inputShellStyle}>
                          <InputLabel htmlFor="buy-price" style={inputLabelStyle}>Price</InputLabel>
                          <OutlinedInput
                            id="buy-price"
                            type={'number'}
                            value={buyPrice || ''}
                            onChange={e => {
                              setBuyPrice(e.target.value);
                            }}
                            endAdornment={<InputAdornment position="end"><span style={adornmentStyle}>{base}</span></InputAdornment>}
                            startAdornment={<InputAdornment position="start"><span style={adornmentStyle}>Price:</span></InputAdornment>}
                            inputProps={{
                              'aria-label': 'price',
                              style: { color: colorScheme.text }
                            }}
                            labelWidth={40}
                          />
                        </FormControl>
                      </Box>
                    )}
                    <Box mb={1}>
                      <FormControl variant="outlined" size={'small'} fullWidth style={inputShellStyle}>
                        <InputLabel htmlFor="buy-amount" style={inputLabelStyle}>Amount</InputLabel>
                        <OutlinedInput
                          id="buy-amount"
                          type="number"
                          value={buyAmount}
                          onChange={e => {
                            setBuyAmount(e.target.value);
                          }}
                          endAdornment={<InputAdornment position="end"><span style={adornmentStyle}>{displaySymbolBuy}</span></InputAdornment>}
                          startAdornment={<InputAdornment position="start"><span style={adornmentStyle}>Amount:</span></InputAdornment>}
                          inputProps={{
                            'aria-label': 'weight',
                            style: { color: colorScheme.text }
                          }}
                          labelWidth={60}
                        />
                      </FormControl>
                    </Box>
                    {isCoinM && !hasShort ? (
                      <Box mb={4} display="flex" alignItems="center" />
                    ) : (
                      <>
                        <span style={{ minWidth: '100%', textAlign: 'left', paddingBottom: '8px', paddingLeft: '2px', display: 'inline-block', color: colorScheme.text }}>
                          {
                            closeBuy ?
                              'Set order amount as % of position' :
                              isCoinM ?
                                'Set order amount as % of coin balance' :
                                'Set order amount as % of base balance'
                          }
                        </span>
                        <Box mb={2} display="flex" alignItems="center">
                          <Box component={'span'} ml={'auto'} display={isMargin ? '' : 'none'}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={closeBuy}
                                  onChange={e => handleCloseBuyCheckbox(e.target.checked)}
                                  name="checkedB"
                                  style={{ color: colorScheme.yellow }}
                                />
                              }
                              label={<span style={{ color: colorScheme.textSubtle }}>Close</span>}
                            />
                          </Box>
                          <Box component={'span'} px={{ xs: 0.5, sm: 1, md: 2 }} py={0.5} mr={1} style={chipStyle} onClick={handleBuyPercent(25)}>25%</Box>
                          <Box component={'span'} px={{ xs: 0.5, sm: 1, md: 2 }} py={0.5} mr={1} style={chipStyle} onClick={handleBuyPercent(50)}>50%</Box>
                          <Box component={'span'} px={{ xs: 0.5, sm: 1, md: 2 }} py={0.5} mr={1} style={chipStyle} onClick={handleBuyPercent(75)}>75%</Box>
                          <Box component={'span'} px={{ xs: 0.5, sm: 1, md: 2 }} py={0.5} mr={1} style={chipStyle} onClick={handleBuyPercent(100)}>100%</Box>
                        </Box>
                      </>
                    )}
                    <Box>
                      {!closeBuy && !(buyAmount && parseFloat(buyAmount) > 0) ? (
                        <Button
                          variant={'contained'}
                          disabled={!(!closeBuy && (buyAmount && parseFloat(buyAmount) > 0))}
                          fullWidth
                          onClick={handleBuyOrder}
                          onMouseEnter={() => setHoverBuy(true)}
                          onMouseLeave={() => setHoverBuy(false)}
                          style={{
                            background: hoverBuy ? colorScheme.greenHover : colorScheme.green,
                            color: '#0B0E11',
                            borderRadius: 8,
                            boxShadow: hoverBuy ? '0 6px 14px rgba(20,199,132,0.15)' : 'none',
                            transform: hoverBuy ? 'translateY(-1px)' : 'translateY(0)',
                            transition: 'all .15s ease-in-out'
                          }}                        >
                          {(closeBuy ? 'Close ' : '') + (isMargin ? 'Long' : 'Buy')}
                        </Button>
                      ) : closeBuy ? (
                        <Button
                          variant={'contained'}
                          disabled={!closeBuy && !(buyAmount && parseFloat(buyAmount) > 0)}
                          fullWidth
                          onClick={handleBuyOrder}
                          onMouseEnter={() => setHoverBuy(true)}
                          onMouseLeave={() => setHoverBuy(false)}
                          style={{
                            background: hoverBuy ? colorScheme.greenHover : colorScheme.green,
                            color: '#0B0E11',
                            borderRadius: 8,
                            boxShadow: hoverBuy ? '0 6px 14px rgba(20,199,132,0.15)' : 'none',
                            transform: hoverBuy ? 'translateY(-1px)' : 'translateY(0)',
                            transition: 'all .15s ease-in-out'
                          }}                        >
                          {(closeBuy ? 'Close ' : '') + (isMargin ? 'Long' : 'Buy')}
                        </Button>
                      ) :
                        (
                          <Button
                            onMouseEnter={() => setHoverBuy(true)}
                            onMouseLeave={() => setHoverBuy(false)}
                            style={{
                              background: hoverBuy ? colorScheme.greenHover : colorScheme.green,
                              color: '#0B0E11',
                              borderRadius: 8,
                              boxShadow: hoverBuy ? '0 6px 14px rgba(20,199,132,0.15)' : 'none',
                              transform: hoverBuy ? 'translateY(-1px)' : 'translateY(0)',
                              transition: 'all .15s ease-in-out'
                            }} variant={'contained'}
                            disabled={!(!closeBuy && (buyAmount && parseFloat(buyAmount) > 0))}
                            fullWidth
                            onClick={handleBuyOrder}
                          >
                            {(closeBuy ? 'Close ' : '') + (isMargin ? 'Long' : 'Buy')}
                          </Button>
                        )}
                    </Box>
                  </Box>
                )}
              </>
              <>
                {showShort && (
                  <Box width={{ xs: '100%' }} mb={2} style={{ padding: '2.5rem', paddingTop: '0rem' }}>
                    {isCoinM ? (
                      <Box display="flex" alignItems="center" mb={2} style={{ color: colorScheme.text }}>
                        <AccountBalanceWallet style={{ color: colorScheme.textSubtle, marginRight: 6 }} /> Contracts: {quoteBalance}
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" mb={2} style={{ color: colorScheme.text }}>
                        <AccountBalanceWallet style={{ color: colorScheme.textSubtle, marginRight: 6 }} /> {quote}: {quoteBalance}
                      </Box>
                    )}
                    {place === 'limit' && (
                      <Box mb={2}>
                        <FormControl variant="outlined" size={'small'} fullWidth style={inputShellStyle}>
                          <InputLabel htmlFor="sell-price" style={inputLabelStyle}>Price</InputLabel>
                          <OutlinedInput
                            id="sell-price"
                            type="number"
                            placeholder={'0.00'}
                            value={sellPrice || ''}
                            autoFocus={true}
                            onChange={e => {
                              setSellPrice(e.target.value);
                            }}
                            endAdornment={<InputAdornment position="end"><span style={adornmentStyle}>{base}</span></InputAdornment>}
                            startAdornment={<InputAdornment position="start"><span style={adornmentStyle}>Price:</span></InputAdornment>}
                            inputProps={{
                              'aria-label': 'weight',
                              style: { color: colorScheme.text }
                            }}
                            labelWidth={40}
                          />
                        </FormControl>
                      </Box>
                    )}
                    <Box mb={1}>
                      <FormControl variant="outlined" size={'small'} fullWidth style={inputShellStyle}>
                        <InputLabel htmlFor="sell-amount" style={inputLabelStyle}>Amount</InputLabel>
                        <OutlinedInput
                          id="sell-amount"
                          type="number"
                          placeholder={'0.00'}
                          value={sellAmount}
                          onChange={e => {
                            setSellAmount(e.target.value);
                          }}
                          endAdornment={<InputAdornment position="end"><span style={adornmentStyle}>{displaySymbolSell}</span></InputAdornment>}
                          startAdornment={<InputAdornment position="start"><span style={adornmentStyle}>Amount:</span></InputAdornment>}
                          inputProps={{
                            'aria-label': 'weight',
                            style: { color: colorScheme.text }
                          }}
                          labelWidth={60}
                        />
                      </FormControl>
                    </Box>
                    {isCoinM && !hasLong ? (
                      <Box mb={4} display="flex" alignItems="center" />
                    ) : (
                      <>
                        <span style={{ minWidth: '100%', textAlign: 'left', paddingBottom: '8px', paddingLeft: '2px', display: 'inline-block', color: colorScheme.text }}>
                          {
                            closeSell ?
                              'Set order amount as % of position' :
                              isCoinM ?
                                'Set order amount as % of coin balance' :
                                exchange?.includes('bybit') ?
                                  'Set order amount as % of base balance' :
                                  'Set order amount as % of quote balance'
                          }
                        </span>
                        <Box mb={2} display="flex" alignItems="center">
                          <Box component={'span'} ml={'auto'} display={isMargin ? '' : 'none'}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={closeSell}
                                  onChange={e => handleCloseSellCheckbox(e.target.checked)}
                                  name="checkedB"
                                  style={{ color: colorScheme.yellow }}
                                />
                              }
                              label={<span style={{ color: colorScheme.textSubtle }}>Close</span>}
                            />
                          </Box>
                          <Box component={'span'} px={{ xs: 0.5, sm: 1, md: 2 }} py={0.5} mr={1} style={chipStyle} onClick={handleSellPercent(25)}>25%</Box>
                          <Box component={'span'} px={{ xs: 0.5, sm: 1, md: 2 }} py={0.5} mr={1} style={chipStyle} onClick={handleSellPercent(50)}>50%</Box>
                          <Box component={'span'} px={{ xs: 0.5, sm: 1, md: 2 }} py={0.5} mr={1} style={chipStyle} onClick={handleSellPercent(75)}>75%</Box>
                          <Box component={'span'} px={{ xs: 0.5, sm: 1, md: 2 }} py={0.5} mr={1} style={chipStyle} onClick={handleSellPercent(100)}>100%</Box>

                        </Box>
                      </>
                    )}
                    <Box>
                      <Button
                        variant={'contained'}
                        disabled={!closeSell && !(sellAmount && parseFloat(sellAmount) > 0)}
                        fullWidth
                        onClick={handleSellOrder}
                        onMouseEnter={() => setHoverSell(true)}
                        onMouseLeave={() => setHoverSell(false)}
                        style={{
                          background: hoverSell ? colorScheme.redHover : colorScheme.red,
                          color: '#FFFFFF',
                          borderRadius: 8,
                          boxShadow: hoverSell ? '0 6px 14px rgba(232,77,79,0.15)' : 'none',
                          transform: hoverSell ? 'translateY(-1px)' : 'translateY(0)',
                          transition: 'all .15s ease-in-out'
                        }}                      >
                        {(closeSell ? 'Close ' : '') + (isMargin ? 'Short' : 'Sell')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            </Box>
          ),
        },
      ];
    });
    setData(temp);
  }, [
    base,
    baseBalance,
    quote,
    quoteBalance,
    buyAmount,
    sellAmount,
    buyPrice,
    sellPrice,
    place,
    closeSell,
    closeBuy,
    handleBuyOrder,
    handleBuyPercent,
    handleSellOrder,
    handleSellPercent,
    isMargin,
  ]);

  useEffect(() => {
    const orderbook = orderBook || {
      ask: [],
      bid: [],
    };
    let spread = 0;
    if (orderbook.ask.length > 0 && orderbook.bid.length > 0) {
      spread = scientificToDecimal(parseFloat((orderbook.ask[0][0] - orderbook.bid[0][0]).toString()).toFixed(8));
    }

    const askVolume = orderbook.ask.map(item => {
      return item[1];
    });
    const bidVolume = orderbook.bid.map(item => {
      return item[1];
    });
    const highestVolume = Math.max(...askVolume, ...bidVolume);
    let backgroundData = {
      ask: [],
      bid: [],
    };
    if (askVolume.length > 0 && bidVolume.length > 0) {
      askVolume.forEach(item => {
        backgroundData.ask.push((item / highestVolume) * 100);
      });

      bidVolume.forEach(item => {
        backgroundData.bid.push((item / highestVolume) * 100);
      });
      setOrderbookBackground(backgroundData);
    }

    setSpread(spread);
    if (spread !== 0) {
      setSpreadPct(((parseFloat(spread) / orderbook.bid[0][0]) * 100).toFixed(2).toString());
    }
  }, [orderBook]);

  useEffect(() => {
    let price = buyPrice;
    if (place === 'market') {
      price = Ask;
    }
  }, [buyPrice, buyAmount, place, Ask, baseBalance]);

  return (
    <Fragment>
      {!showSinglePairInterface ? (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
            <Box fontSize={'16px'} fontWeight={500} style={{ color: colorScheme.text }} whiteSpace={'nowrap'}>
              Trading Terminal
            </Box>
          </AccordionSummary>
          <AccordionDetails style={{ padding: '0px', backgroundColor: '#2a2a2a' }}>
            <Box mt={1}>
              {/* segmented control: Limit / Market */}
              <Box mb={2} px={2}>
                <Box style={segWrapStyle}>
                  <Button
                    size="small"
                    onClick={() => setPlace('limit')}
                    style={{
                      ...segBtnBase,
                      background: place === 'limit' ? colorScheme.tileHover : 'transparent',
                      color: place === 'limit' ? colorScheme.text : colorScheme.textSubtle,
                      border: `1px solid ${place === 'limit' ? colorScheme.borderSoft : 'transparent'}`
                    }}
                  >
                    Limit
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setPlace('market')}
                    style={{
                      ...segBtnBase,
                      background: place === 'market' ? colorScheme.tileHover : 'transparent',
                      color: place === 'market' ? colorScheme.text : colorScheme.textSubtle,
                      border: `1px solid ${place === 'market' ? colorScheme.borderSoft : 'transparent'}`
                    }}
                  >
                    Market
                  </Button>
                </Box>
              </Box>
              {/* render current panel content */}
              {data[place] && data[place][0] && data[place][0].component}
            </Box>
          </AccordionDetails>
        </Accordion>
      ) : null}

      <Accordion expanded={orderBookExpand} onChange={() => setOrderBookExpand(!orderBookExpand)}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
          <Box fontSize={'16px'} fontWeight={500} style={{ color: colorScheme.text }} whiteSpace={'nowrap'}>
            Order Book
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%" >
            <Box display="flex" justifyContent="end" alignItems="center" mb={1}>
              <Typography variant="caption" style={{ color: colorScheme.textSubtle }}>
                Spread: <span style={{ color: colorScheme.yellow }}>${spread}</span> <span style={{ color: colorScheme.yellow }}>({spreadPct}%)</span>
              </Typography>
            </Box>

            <Box display="flex" mb={0.5}>
              <Box width="33%" textAlign="left">
                <Typography variant="caption" style={{ color: colorScheme.textSubtle }}>Price</Typography>
              </Box>
              <Box width="33%" textAlign="right">
                <Typography variant="caption" style={{ color: colorScheme.textSubtle }}>Amount</Typography>
              </Box>
              <Box width="33%" textAlign="right">
                <Typography variant="caption" style={{ color: colorScheme.textSubtle }}>Total</Typography>
              </Box>
            </Box>

            <Box overflow="auto">
              <Box>
                {orderBook && orderBook.ask.slice(0, 5).reverse().map((ask, i) => {
                  const correctIndex = orderBook.ask.slice(0, 5).length - 1 - i;
                  return (
                    <Box key={i} display="flex" position="relative" mb={0.25}>
                      <Box
                        position="absolute"
                        left={0}
                        top={0}
                        bottom={0}
                        style={{ background: '#F6465D33', width: `${orderbookBackground.ask[correctIndex]}%` }}
                      />
                      <Box width="33%" textAlign="left">
                        <Typography variant="caption" style={{ color: colorScheme.red }}>
                          {scientificToDecimal(ask[0])}
                        </Typography>
                      </Box>
                      <Box width="33%" textAlign="right">
                        <Typography variant="caption" style={{ color: '#E0E3EB' }}>
                          {ask[1]}
                        </Typography>
                      </Box>
                      <Box width="33%" textAlign="right">
                        <Typography variant="caption" style={{ color: '#E0E3EB' }}>
                          {(ask[0] * ask[1]).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box my={0.5} height="1px" style={{ background: colorScheme.divider }} />

              <Box>
                {orderBook && orderBook.bid.slice(0, 5).map((bid, key) => (
                  <Box key={key} display="flex" position="relative" mb={0.25}>
                    <Box position="absolute" left={0} top={0} bottom={0} style={{ background: '#0ECB8133', width: `${orderbookBackground.bid[key]}%` }} />
                    <Box width="33%" textAlign="left">
                      <Typography variant="caption" style={{ color: colorScheme.green }}>{scientificToDecimal(bid[0])}</Typography>
                    </Box>
                    <Box width="33%" textAlign="right">
                      <Typography variant="caption" style={{ color: '#E0E3EB' }}>{bid[1]}</Typography>
                    </Box>
                    <Box width="33%" textAlign="right">
                      <Typography variant="caption" style={{ color: '#E0E3EB' }}>{(bid[0] * bid[1]).toFixed(2)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
          <Box fontSize={'16px'} fontWeight={500} style={{ color: colorScheme.text }} whiteSpace={'nowrap'}>
            Trading History
            <CategoryPicker
              onChange={handleHeadsTradingHistory}
              allCategories={labelsTradingHistory}
              categories={tradingHistoryHeads}
              small={true}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <ServerSideTable
            exchange={exchange}
            pair={pair}
            style={{ minWidth: '100%', maxWidth: '100%' }}
            heads={tradingHistoryHeads}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
          <Box fontSize={'16px'} fontWeight={500} style={{ color: colorScheme.text }} whiteSpace={'nowrap'}>
            Open Orders ({currentOpenOrders.length})
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box mt={-4} mb={-1} width={'100%'} minHeight={'100px'}>
            <Table
              size={'small'}
              tableHead={['Rate', 'Amount', 'Time', 'Cancel order']}
              tableData={currentOpenOrders}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
}

const labelsTradingHistory = [
  {
    title: 'Rate',
    type: 'number',
    tooltip: 'Order fill rate',
  },
  {
    title: 'Pnl',
    type: 'number',
    tooltip: 'Order profit and loss',
  },
  {
    title: 'Amount',
    type: 'number',
    tooltip: 'Order amount',
  },
  {
    title: 'Amount (base)',
    type: 'number',
    tooltip: 'Order amount (base)',
  },
  {
    title: 'Time',
    type: 'number',
    tooltip: 'Order fill time',
  },
];
